<script setup lang="ts">
import { computed } from 'vue';

import { useConfirm } from '@/shared/lib/confirm/use-confirm';

import AppButton from './button/AppButton.vue';
import BaseModal from './modal/BaseModal.vue';
import AppTextarea from './textarea/AppTextarea.vue';

const { canConfirm, inputValue, isOpen, options, handleConfirm, handleAlternative, handleCancel } = useConfirm();
type Action = 'confirm' | 'alternative' | 'cancel';

const actions = computed((): readonly Action[] => {
  // Отмена такого диалога — крестик и Esc: третья кнопка рядом с двумя выходами лишняя.
  if (options.value?.alternativeText) return ['alternative', 'confirm'];

  return options.value?.primaryAction === 'cancel' ? ['confirm', 'cancel'] : ['cancel', 'confirm'];
});
const ACTION_HANDLERS: Record<Action, () => void> = {
  confirm: handleConfirm,
  alternative: handleAlternative,
  cancel: handleCancel,
};
const actionText = (action: Action): string | undefined =>
  action === 'confirm'
    ? options.value?.confirmText
    : action === 'alternative'
      ? options.value?.alternativeText
      : options.value?.cancelText;
</script>

<template>
  <BaseModal
    :show="isOpen"
    size="sm"
    @close="handleCancel"
  >
    <template #title>{{ options?.title }}</template>

    <p class="text-(--text-primary)">{{ options?.message }}</p>

    <label
      v-if="options?.input"
      class="mt-4 block text-sm font-medium text-(--text-primary)"
    >
      {{ options.input.label }}
      <AppTextarea
        v-model="inputValue"
        class="mt-2"
        data-autofocus
        :placeholder="options.input.placeholder"
      />
    </label>

    <!-- Фокус на отмене: случайный Enter не подтверждает действие. У ввода причины фокус получает поле. -->
    <!-- Два выхода — кнопки одной ширины. -->
    <div
      class="mt-6 gap-3"
      :class="options?.alternativeText ? 'grid grid-cols-2' : 'flex justify-end'"
    >
      <AppButton
        v-for="(action, index) in actions"
        :key="action"
        :outline="index < actions.length - 1"
        :disabled="action === 'confirm' && !canConfirm"
        :data-autofocus="action === 'cancel' && !options?.input ? '' : undefined"
        @click="ACTION_HANDLERS[action]()"
      >
        {{ actionText(action) }}
      </AppButton>
    </div>
  </BaseModal>
</template>
